import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <img src="/logo.png" alt="Innings Pro" className="h-8 sm:h-9 w-auto" />
              <span className="text-lg sm:text-xl font-semibold text-gray-900">Innings Pro</span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <Link
                to="/"
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Back to Home</span>
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center px-3 sm:px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center mr-4">
              <Shield className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900">Privacy Policy</h1>
              <p className="text-gray-600 mt-1">Last updated: December 21, 2025</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-gray max-w-none">
            <p className="text-lg text-gray-600 mb-8">
              Welcome to Innings Pro ("we", "our", or "us"). Your privacy is important to us. This Privacy Policy explains how we collect, use, store, and protect your information when you use our website and services.
            </p>

            <p className="text-gray-600 mb-8">
              By using our website, you agree to the practices described in this Privacy Policy.
            </p>

            {/* Section 1 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              <p className="text-gray-600 mb-4">We may collect the following types of information:</p>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">a. Information You Provide</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Account details (such as username, email address, and password)</li>
                <li>Organization information (organization name, contact details)</li>
                <li>Tournament details (tournament name, format, dates, location)</li>
                <li>Team information (team names, logos, squad details)</li>
                <li>Player information (player names, roles, statistics)</li>
                <li>Match details and live scores you enter</li>
                <li>Venue and umpire information</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-3">b. Automatically Collected Information</h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>IP address and location data</li>
                <li>Browser type, version, and device information</li>
                <li>Pages visited and actions taken on the website</li>
                <li>Date, time, and duration of access</li>
                <li>Referring website or source</li>
              </ul>
            </div>

            {/* Section 2 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">We use the collected information to:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Provide and maintain our cricket tournament management services</li>
                <li>Allow users to create and manage tournaments, teams, and players</li>
                <li>Display matches, scorecards, and live scores</li>
                <li>Generate statistics, points tables, and analytics</li>
                <li>Maintain and improve website functionality and performance</li>
                <li>Ensure website security and prevent misuse or fraud</li>
                <li>Communicate important updates, service announcements, or notices</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            {/* Section 3 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Sharing of Information</h2>
              <p className="text-gray-600 mb-4">
                We <strong>do not sell or rent</strong> your personal information to third parties.
              </p>
              <p className="text-gray-600 mb-4">We may share information only:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>When required by law, legal process, or government request</li>
                <li>To protect the rights, safety, or security of our users, website, or the public</li>
                <li>With trusted service providers who help operate the website (under strict confidentiality agreements)</li>
                <li>In connection with a merger, acquisition, or sale of assets (with notice to users)</li>
              </ul>
            </div>

            {/* Section 4 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Publicly Visible Information</h2>
              <p className="text-gray-600 mb-4">Please be aware that:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Tournament names, team names, player names, match results, and live scores may be visible to other users or the public through our public scoreboard feature</li>
                <li>Information shared via public URLs can be accessed without login</li>
                <li>We recommend avoiding submitting sensitive personal information (such as personal addresses or identification numbers) in player profiles or tournament details</li>
              </ul>
            </div>

            {/* Section 5 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Data Storage and Security</h2>
              <p className="text-gray-600 mb-4">We take reasonable measures to protect your data, including:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Secure servers with encryption for data transmission (HTTPS/SSL)</li>
                <li>Password hashing and secure authentication mechanisms</li>
                <li>Access controls limiting data access to authorized personnel only</li>
                <li>Regular security assessments and updates</li>
                <li>Secure backup procedures</li>
              </ul>
              <p className="text-gray-600 mt-4">
                However, no method of electronic storage or transmission over the internet is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee absolute security.
              </p>
            </div>

            {/* Section 6 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Data Retention</h2>
              <p className="text-gray-600 mb-4">
                We retain your personal information for as long as your account is active or as needed to provide you services. We may also retain and use your information as necessary to:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Comply with legal obligations</li>
                <li>Resolve disputes</li>
                <li>Enforce our agreements</li>
                <li>Maintain business records</li>
              </ul>
              <p className="text-gray-600 mt-4">
                You may request deletion of your account and associated data by contacting us.
              </p>
            </div>

            {/* Section 7 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Cookies and Tracking Technologies</h2>
              <p className="text-gray-600 mb-4">We may use cookies and similar technologies to:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Keep you signed in to your account</li>
                <li>Remember your preferences and settings</li>
                <li>Improve user experience</li>
                <li>Analyze website usage and performance</li>
                <li>Ensure security and prevent fraud</li>
              </ul>
              <p className="text-gray-600 mt-4">
                You can control cookies through your browser settings. Disabling cookies may affect certain features of our website.
              </p>
            </div>

            {/* Section 8 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Third-Party Services</h2>
              <p className="text-gray-600 mb-4">
                Our website may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any personal information.
              </p>
            </div>

            {/* Section 9 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Children's Privacy</h2>
              <p className="text-gray-600 mb-4">
                Our website is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately so we can take steps to remove such information.
              </p>
            </div>

            {/* Section 10 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Your Rights</h2>
              <p className="text-gray-600 mb-4">Depending on your location, you may have the right to:</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate or incomplete data</li>
                <li>Request deletion of your personal data</li>
                <li>Object to or restrict certain processing of your data</li>
                <li>Request data portability (receive your data in a structured format)</li>
                <li>Withdraw consent where processing is based on consent</li>
                <li>Lodge a complaint with a supervisory authority</li>
              </ul>
              <p className="text-gray-600 mt-4">
                To exercise any of these rights, please contact us using the information provided below.
              </p>
            </div>

            {/* Section 11 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. International Data Transfers</h2>
              <p className="text-gray-600">
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using our services, you consent to such transfers. We will take appropriate safeguards to ensure your data remains protected.
              </p>
            </div>

            {/* Section 12 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-600">
                We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. Any changes will be posted on this page with an updated effective date. We encourage you to review this policy periodically. Your continued use of our services after changes are posted constitutes your acceptance of the updated policy.
              </p>
            </div>

            {/* Section 13 */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
              <p className="text-gray-600 mb-4">
                If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us at:
              </p>
              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <p className="text-gray-800 font-semibold mb-2">Innings Pro</p>
                <p className="text-gray-600">
                  Website: <a href="https://www.inningspro.com" className="text-green-600 hover:text-green-700 transition-colors">www.inningspro.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-6 md:mb-0">
              <img src="/logo.png" alt="Innings Pro" className="h-8 w-auto" />
              <span className="text-lg font-semibold text-white">Innings Pro</span>
            </div>
            <div className="flex items-center space-x-8 text-sm">
              <Link to="/" className="text-gray-400 hover:text-white transition-colors">
                Home
              </Link>
              <Link to="/login" className="text-gray-400 hover:text-white transition-colors">
                Sign In
              </Link>
              <Link to="/register" className="text-gray-400 hover:text-white transition-colors">
                Register
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm text-gray-500">
            © {new Date().getFullYear()} Innings Pro. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
